import api, { ResponsePayloadWithData } from '.';
import { ModulePublishingState } from '../@types/module-publishing-state';
import { replaceSoftHyphenWithUnicode } from '../utils/replaceSoftHyphenWithUnicode';

export const MEDIALINK_SORTING_OPTION = {
  CATEGORY: 'category',
  LESSON: 'lesson_order',
} as const;

export type MedialinkSorting = typeof MEDIALINK_SORTING_OPTION[keyof typeof MEDIALINK_SORTING_OPTION];

export type Module = {
  id: string;
  name: string;
  niceName: string;
  catalogName: string;
  slug: string;
  methodSlug: string;
  subjectName: string;
  subjectSlug: string;
  years: string[];
  weight: number;
  incomplete: boolean;
  hasExercises: boolean;
  hasExerciseEntrance: boolean;
  hostedExternally: boolean;
  subModules: string[];
  /**
   * Product license fields are only present for the license products configured on the module:
   * `undefined` means the product is not configured, `false` means it is configured but not licensed.
   */
  licenses: {
    hasDemoProductLicenseForTeacher?: boolean;
    hasDigitalManualLicenseForTeacher?: boolean;
    hasDocumentLicenseForTeacher?: boolean;
    hasDocumentLicenseForStudent?: boolean;
    isActiveBook: boolean;
    hasBaollLicense?: boolean;
  };
  isStudentModule?: boolean;
  bookType?: string;
  releaseDate?: string;
  styling?: {
    logo?: string;
    visual?: string;
    cover?: string;
    primaryColor?: string;
  };
  externalContentUrl?: string;
  enableSlideSetsAccess: boolean;
  isExerciseModule: boolean;
  publishingState: ModulePublishingState;
  counts: {
    slideSets?: number;
    studentExercises: number;
    teacherExercises: number;
    studentOtherMaterials: number;
    teacherOtherMaterials: number;
  };
  superModuleId?: string;
};

export const getModuleBySlug = async (moduleSlug: string) => {
  const response = await api.get<Module>(`/studio/modules/slug/${moduleSlug}`);

  response.data.catalogName = replaceSoftHyphenWithUnicode(response.data.catalogName);

  return response;
};

export const getSubModulesForModule = async (moduleId: string) => {
  const { data } = await api.get<ResponsePayloadWithData<Module[]>>(`/studio/modules/${moduleId}/sub-modules`);

  data.data.forEach(module => {
    module.catalogName = replaceSoftHyphenWithUnicode(module.catalogName);
  });

  return data;
};

export async function getModuleById(moduleId: string) {
  const { data } = await api.get<Module>(`/studio/modules/${moduleId}`);

  data.catalogName = replaceSoftHyphenWithUnicode(data.catalogName);

  return data;
}
